import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft, Images, X, ChevronLeft, ChevronRight, Download, ZoomIn } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const GalleryEvent = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    fetchGalleryEvent();
  }, [slug]);

  const fetchGalleryEvent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/gallery/events/${slug}`);
      setEvent(response.data);
    } catch (error) {
      // Gallery event not found - redirect handled below
      navigate('/gallery');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Keyboard navigation for lightbox
  const handleKeyDown = useCallback((e) => {
    if (lightboxIndex === null || !event?.images) return;
    
    if (e.key === 'ArrowLeft') {
      setLightboxIndex(prev => prev > 0 ? prev - 1 : event.images.length - 1);
    } else if (e.key === 'ArrowRight') {
      setLightboxIndex(prev => prev < event.images.length - 1 ? prev + 1 : 0);
    } else if (e.key === 'Escape') {
      setLightboxIndex(null);
    }
  }, [lightboxIndex, event?.images]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [lightboxIndex]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007A7A] mx-auto mb-4"></div>
          <p className="text-gray-500">Loading gallery...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="text-center">
          <Images className="w-20 h-20 mx-auto text-gray-300 mb-6" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-3">Gallery Not Found</h2>
          <Link to="/gallery" className="text-[#007A7A] hover:underline">
            Return to Gallery
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="gallery-event-page">
      {/* Hero Section */}
      <section className="relative py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          {event.cover_image ? (
            <img 
              src={resolveImageUrl(event.cover_image)}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#0A345E] to-[#007A7A]"></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A345E]/85 to-[#007A7A]/75"></div>
        </div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          {/* Back Button */}
          <Link 
            to="/gallery" 
            className="inline-flex items-center text-white/90 hover:text-white mb-6 transition-colors"
            data-testid="back-to-gallery-link"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Gallery
          </Link>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{event.title}</h1>
          
          <div className="flex flex-wrap items-center gap-4 text-white/90">
            {event.event_date && (
              <div className="flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                {formatDate(event.event_date)}
              </div>
            )}
            <div className="flex items-center">
              <Images className="w-5 h-5 mr-2" />
              {event.image_count || 0} photos
            </div>
          </div>

          {event.description && (
            <p className="mt-4 text-lg text-white/90 max-w-3xl">{event.description}</p>
          )}
        </div>
      </section>

      {/* Photo Grid */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          {event.images && event.images.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="photo-grid">
              {event.images.map((image, index) => (
                <div
                  key={image.id}
                  className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                  onClick={() => setLightboxIndex(index)}
                  data-testid={`photo-${image.id}`}
                >
                  <img
                    src={resolveImageUrl(image.image_path)}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Images className="w-20 h-20 mx-auto text-gray-300 mb-6" />
              <h2 className="text-xl font-semibold text-gray-700 mb-3">No Photos Yet</h2>
              <p className="text-gray-500">Photos from this event will be added soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && event.images && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxIndex(null)}
          data-testid="lightbox"
        >
          {/* Close Button */}
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 rounded-full p-2 text-white transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 text-white/70 text-sm">
            {lightboxIndex + 1} / {event.images.length}
          </div>

          {/* Main Image */}
          <div 
            className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={resolveImageUrl(event.images[lightboxIndex].image_path)}
              alt={`Photo ${lightboxIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>

          {/* Navigation Arrows */}
          {event.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(prev => prev > 0 ? prev - 1 : event.images.length - 1);
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-3 text-white transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(prev => prev < event.images.length - 1 ? prev + 1 : 0);
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-3 text-white transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          {/* Thumbnail Strip */}
          {event.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto py-2 px-4">
              {event.images.map((image, index) => (
                <button
                  key={image.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(index);
                  }}
                  className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                    index === lightboxIndex 
                      ? 'border-white opacity-100' 
                      : 'border-transparent opacity-50 hover:opacity-75'
                  }`}
                >
                  <img
                    src={resolveImageUrl(image.image_path)}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* More Galleries Section */}
      <section className="py-12 bg-[#F5F2E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[#0A345E] mb-4">Explore More Galleries</h2>
          <Link 
            to="/gallery" 
            className="inline-flex items-center px-6 py-3 bg-[#007A7A] text-white font-semibold rounded-lg hover:bg-[#007A7A]/90 transition-colors"
          >
            <Images className="w-5 h-5 mr-2" />
            View All Galleries
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GalleryEvent;
